// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Click-to-define terms. The audience is a developer, so most jargon needs no
 * gloss — these are the handful where the tool takes a position that would
 * otherwise look arbitrary, and the two that are routinely misread as verdicts.
 */

export const GLOSSARY: Record<string, { title: string; body: string }> = {
  slopsquatting: {
    title: 'Slopsquatting',
    body: 'Registering a package under a name that language models reliably invent. It works because the hallucinated names are stable: ask for the same thing twice and you often get the same non-existent package, so a squatter can register it and wait. It is typosquatting where the typo is made by the model rather than by your fingers.',
  },
  'edit-distance': {
    title: 'Edit distance',
    body: 'How many single-character changes turn one name into the other — insert, delete, substitute, or swap two adjacent characters. ghostdep charges half as much for substituting a key for one physically next to it on the keyboard, because `reqeusts` is a likelier slip than `rezuests`.',
  },
  budget: {
    title: 'Edit budget',
    body: 'How far apart two names can be before ghostdep stops calling them similar, scaled by length. One edit in `ws` is half the name; one edit in `python-dateutil` is nothing. Without this, `ms`, `qs` and `ws` — three real and hugely popular packages, each one edit from the others — would generate a warning every time.',
  },
  ratio: {
    title: 'Popularity ratio',
    body: 'How much more popular a neighbouring name must be before ghostdep mentions it. A squat imitates something bigger than itself; two real packages of similar size with similar names are just two real packages. Not applied to a name that does not exist at all, where the list is a "did you mean" rather than a comparison.',
  },
  integrity: {
    title: 'Integrity hash',
    body: "A digest of the exact archive a registry will serve you, so your installer can prove it got the bytes it expected. npm calls it `integrity` and keeps it in package-lock.json; pip calls it `--hash` and expects it in requirements.txt.",
  },
  'require-hashes': {
    title: '--require-hashes',
    body: 'A pip mode where every package installed must be pinned to an exact version and carry its hash — transitive dependencies included. That last part is why the requirements.txt ghostdep writes is usually not enough on its own: it only knows the names you pasted. Run it through pip-compile --generate-hashes for a complete lock.',
  },
  'install-script': {
    title: 'Install script',
    body: 'Code a package runs on your machine at install time, before you have imported anything or run a line of your own. It is the mechanism most supply-chain attacks actually use. Having one is completely ordinary — native modules need them — so ghostdep reports it as a fact rather than as a warning.',
  },
  'first-published': {
    title: 'First published',
    body: 'When the earliest version of this package appeared. A package created days ago whose name is one character from something huge is the shape of a squat. It is also the shape of a brand-new project by an honest author, which is why this is shown as a date rather than as a verdict.',
  },
  'not-checked': {
    title: 'Not checked',
    body: 'The lookup failed — offline, rate-limited, or the registry returned an error. This is deliberately a different state from "does not exist". Reporting a dropped request as a missing package would be the single most damaging bug this tool could have.',
  },
};

export function mountGlossary(): () => void {
  let tip: HTMLDivElement | null = null;

  const close = () => {
    tip?.remove();
    tip = null;
  };

  const onPointer = (event: Event) => {
    const target = (event.target as HTMLElement | null)?.closest?.('.glossary-link') as HTMLElement | null;
    if (!target) {
      close();
      return;
    }
    event.preventDefault();
    const entry = GLOSSARY[target.dataset.term ?? ''];
    if (!entry) return;
    close();
    tip = document.createElement('div');
    tip.className = 'glossary-tip';
    tip.setAttribute('role', 'tooltip');
    const strong = document.createElement('strong');
    strong.textContent = entry.title;
    tip.append(strong, document.createTextNode(entry.body));
    document.body.append(tip);
    const rect = target.getBoundingClientRect();
    const width = Math.min(340, window.innerWidth - 24);
    tip.style.width = `${width}px`;
    tip.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
    const below = rect.bottom + 8;
    tip.style.top = below + tip.offsetHeight > window.innerHeight ? `${Math.max(12, rect.top - tip.offsetHeight - 8)}px` : `${below}px`;
  };

  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') close();
  };

  document.addEventListener('pointerdown', onPointer);
  document.addEventListener('keydown', onKey);
  window.addEventListener('resize', close);

  return () => {
    document.removeEventListener('pointerdown', onPointer);
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', close);
    close();
  };
}
